import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import { FlowContainer, FlowNode, FlowArrow, FlowTerminal, FlowNote, FlowParallel } from '../FlowPrimitives';

// ============================================================================
// Experiment 9 – Spectrophotometric Determination of Iron in Soil
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp9',
  title: 'Experiment 9: Spectrophotometric Determination of Iron in Soil',
  objectives: [
    'Extract iron from soil using acid digestion with HCl',
    'Apply reduction chemistry to convert Fe\u00b3⁺ to Fe\u00b2⁺',
    'Form the iron-phenanthroline complex for colorimetric measurement',
    'Determine iron content in soil spectrophotometrically at 510 nm',
  ],
  safetyNotes: [
    'Concentrated HCl is corrosive — handle in fume hood, wear gloves and goggles',
    'Hot acid — severe burn hazard; use tongs and heat-resistant gloves',
    'Work in fume hood during the entire soil extraction / digestion step',
    'Dispose of all chemical waste in designated containers',
  ],
  equipment: [
    'Spec 20 spectrophotometer',
    'Hot plate',
    'Volumetric flasks (100 mL)',
    'Filter apparatus (funnel, filter paper)',
    'Beakers (250 mL)',
    'Watch glass',
    'Pipettes (various sizes)',
    'Analytical balance',
  ],
  chemicals: [
    'Concentrated HCl',
    'Hydroxylamine hydrochloride solution',
    'Sodium acetate buffer (pH 3.5)',
    '0.1% 1,10-phenanthroline solution',
    'Iron standard solution (1000 ppm Fe stock, diluted to 10 ppm working stock)',
    'Distilled water',
    'Soil samples',
  ],
  procedure: [
    {
      number: 1,
      title: 'Extract Iron from Soil',
      details: [
        'Weigh 1.00 g soil sample accurately on analytical balance',
        'Transfer to a 250 mL beaker',
        'Add 5 mL of concentrated HCl and 5 mL of water',
        'Cover with watch glass and simmer gently for 15 minutes',
        'Filter while hot through filter paper',
        'Wash residue with hot distilled water',
        'Transfer filtrate to 100 mL volumetric flask and dilute to mark',
      ],
      safetyNote: 'Concentrated HCl — add in fume hood; hot acid is a burn hazard',
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 2,
      title: 'Prepare Standards',
      details: [
        'Prepare a blank (0 ppm) and six standards: 0.10, 0.50, 1.0, 2.0, 3.0, 5.0 ppm Fe',
        'Pipette required volume of 10 ppm stock into 50 mL or 100 mL volumetric flasks',
        'Use C1V1 = C2V2 to calculate stock volumes needed',
      ],
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 3,
      title: 'Add Hydroxylamine (Reduce Fe\u00b3⁺ → Fe\u00b2⁺)',
      details: [
        'Add 1 mL hydroxylamine hydrochloride solution to each flask',
        'This reduces all Fe\u00b3⁺ to Fe\u00b2⁺ so it can form the coloured complex',
        'Reaction: 2Fe\u00b3⁺ + 2NH₂OH → 2Fe\u00b2⁺ + N₂O + 4H⁺ + H₂O',
      ],
      manualRef: 'Lab Manual p.2',
    },
    {
      number: 4,
      title: 'Add Buffer and Phenanthroline',
      details: [
        'Add 10 mL sodium acetate buffer (pH 3.5) to each flask',
        'Add 5 mL of 0.1% 1,10-phenanthroline solution',
        'Dilute to the 100 mL mark with distilled water',
        'Mix thoroughly',
      ],
      manualRef: 'Lab Manual p.2',
    },
    {
      number: 5,
      title: 'Develop Color and Measure',
      details: [
        'Wait 10 minutes for full color development of the orange-red complex',
        'Set spectrophotometer wavelength to 510 nm',
        'Zero the instrument with the blank',
        'Measure absorbance of all standards and soil extract',
      ],
      manualRef: 'Lab Manual p.2',
    },
    {
      number: 6,
      title: 'Calculate Iron Content',
      details: [
        'Plot calibration curve: absorbance vs. [Fe] in ppm',
        '[Fe] in solution = (Absorbance – intercept) / slope',
        'Total Fe (mg) = [Fe]_solution × extract volume (L)',
        '%Fe = (total Fe mg / sample mass mg) × 100',
        'ppm Fe in soil = (total Fe mg / sample mass g) × 1000',
      ],
      manualRef: 'Lab Manual p.2',
    },
  ],
  keyFormulas: [
    'Extraction: Fe₂O₃ + 6HCl → 2FeCl₃ + 3H₂O',
    'Reduction: 2Fe\u00b3⁺ + 2NH₂OH → 2Fe\u00b2⁺ + N₂O + 4H⁺ + H₂O',
    'Complex: Fe\u00b2⁺ + 3 phen → [Fe(phen)₃]\u00b2⁺ (orange-red, λmax = 510 nm)',
    '[Fe] = (Absorbance – intercept) / slope',
    '%Fe = (total Fe mg / sample mass mg) × 100',
    'ppm Fe = (total Fe mg / sample mass g) × 1000',
  ],
  tips: [
    'Iron must be in the Fe\u00b2⁺ state — only Fe\u00b2⁺ forms the coloured phenanthroline complex',
    'The pH buffer (pH 3.5) is essential for maximum complex stability',
    'Filter the soil extract while hot for best iron recovery',
    'If sample absorbance exceeds the highest standard, dilute and re-measure',
    'Always zero the spectrophotometer with the blank before measurements',
    'Use the same Spec 20 cuvette for all readings; condition with next solution after each reading',
  ],
};

// ============================================================================
// Flowchart — Iron Determination Procedure
// ============================================================================

function Flowchart() {
  const nodeW = 180;
  const nodeH = 56;
  const cx = 210; // left edge of centred node
  const startY = 44;
  const gap = 76;

  const row = (i: number) => startY + i * gap;

  return (
    <FlowContainer width={600} height={700} title="Iron Determination in Soil">
      {/* Start */}
      <FlowTerminal x={230} y={row(0)} width={140} height={36} label="Start" color="#6366f1" />

      {/* Step 1 — Extract Iron */}
      <FlowArrow from={{ x: 300, y: row(0) + 36 }} to={{ x: 300, y: row(1) }} />
      <FlowNode x={cx} y={row(1)} width={nodeW} height={nodeH} title="Extract Iron" subtitle="Conc HCl + water, simmer 15 min, filter" color="#0d9488" />
      <FlowNote x={400} y={row(1) + 4} text="Work in fume hood" width={130} color="#ef4444" />

      {/* Step 2 — Standards */}
      <FlowArrow from={{ x: 300, y: row(1) + nodeH }} to={{ x: 300, y: row(2) }} />
      <FlowNode x={cx} y={row(2)} width={nodeW} height={nodeH} title="Prepare Standards" subtitle="0.10–5.0 ppm Fe from 10 ppm stock" color="#0d9488" />

      {/* Step 3 — Add Hydroxylamine */}
      <FlowArrow from={{ x: 300, y: row(2) + nodeH }} to={{ x: 300, y: row(3) }} />
      <FlowNode x={cx} y={row(3)} width={nodeW} height={nodeH} title="Add Hydroxylamine" subtitle="Reduce Fe\u00b3⁺ → Fe\u00b2⁺" color="#0d9488" />

      {/* Step 4 — Add Phenanthroline */}
      <FlowArrow from={{ x: 300, y: row(3) + nodeH }} to={{ x: 300, y: row(4) }} />
      <FlowNode x={cx} y={row(4)} width={nodeW} height={nodeH} title="Add Phenanthroline" subtitle="+ acetate buffer pH 3.5" color="#0d9488" />

      {/* Step 5 — Develop Color */}
      <FlowArrow from={{ x: 300, y: row(4) + nodeH }} to={{ x: 300, y: row(5) }} />
      <FlowNode x={cx} y={row(5)} width={nodeW} height={nodeH} title="Develop Color" subtitle="Wait 10 min, λ = 510 nm" color="#f59e0b" />
      <FlowNote x={400} y={row(5) + 4} text="10 min wait" width={120} color="#f59e0b" />

      {/* Step 6 — Measure Absorbance */}
      <FlowArrow from={{ x: 300, y: row(5) + nodeH }} to={{ x: 300, y: row(6) }} />
      <FlowNode x={cx} y={row(6)} width={nodeW} height={nodeH} title="Measure Absorbance" subtitle="Zero with blank, read all" color="#0d9488" />

      {/* Step 7 — Calculate */}
      <FlowArrow from={{ x: 300, y: row(6) + nodeH }} to={{ x: 300, y: row(7) }} />
      <FlowNode x={cx} y={row(7)} width={nodeW} height={nodeH} title="Calculate %Fe / ppm" subtitle="Calibration curve method" color="#0d9488" />

      {/* End */}
      <FlowArrow from={{ x: 300, y: row(7) + nodeH }} to={{ x: 300, y: row(7) + nodeH + 20 }} />
      <FlowTerminal x={230} y={row(7) + nodeH + 20} width={140} height={36} label="Done" color="#6366f1" />
    </FlowContainer>
  );
}

// ============================================================================
// Default Export
// ============================================================================

export default function Exp9Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
